import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import { hasAnyOfflineQueue } from '../../utils/offlineNoteQueue';

/**
 * Bağlantı durumu şeridi (uygulama chrome'unun altında, tam genişlik).
 *
 * Politika (83. oturum):
 * - Çevrimdışı: kişisel notlar bu cihazda kaydedilmeye devam eder, ortak çalışma salt okunur.
 * - Bağlantı geri geldiğinde tek seferlik güvenli reload YAPILIR, ancak yalnızca
 *   kişisel offline kuyruğu sunucuya tamamen yazıldıktan sonra.
 *   (Kuyruk sync'lenmeden reload → yerel değişiklik kaybı riski.)
 *
 * Görünüm (84. oturum): Çevrimdışı durumu artık burada yatay banner olarak GÖSTERİLMEZ —
 * Header'daki `[WifiOff] Çevrimdışı` göstergesi + tooltip bu işi yapıyor
 * (`shared:connectionBanner.message` tooltip metni olarak kullanılıyor).
 * Bu bileşen yine mount kalır: reload kapısı ve sync durum şeritleri (syncing/syncFailed) burada.
 */
const OFFLINE_SESSION_KEY = 'notesplus_offline_session';

const ConnectionMonitorBanner = () => {
  const { t } = useTranslation('shared');
  const { isServerOnline } = useConnectionMonitor();
  const [syncPending, setSyncPending] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const reloadTriggeredRef = useRef(false);

  const reloadOnce = useCallback(() => {
    if (reloadTriggeredRef.current) return;
    reloadTriggeredRef.current = true;
    try {
      sessionStorage.setItem(OFFLINE_SESSION_KEY, 'reloaded');
    } catch { /* ignore */ }
    window.location.reload();
  }, []);

  // Çevrimdışı oturum işareti + bağlantı dönüşünde reload kapısı
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let flag = null;
    try { flag = sessionStorage.getItem(OFFLINE_SESSION_KEY); } catch { /* ignore */ }

    if (!isServerOnline) {
      try { sessionStorage.setItem(OFFLINE_SESSION_KEY, '1'); } catch { /* ignore */ }
      setSyncFailed(false);
      return;
    }

    // Reload sonrası: işareti temizle
    if (flag === 'reloaded') {
      try { sessionStorage.removeItem(OFFLINE_SESSION_KEY); } catch { /* ignore */ }
      return;
    }

    if (flag !== '1') return;

    // Bekleyen kişisel not varsa reload'u senkron tamamlanana kadar BEKLET
    if (hasAnyOfflineQueue()) {
      setSyncPending(true);
      return;
    }

    reloadOnce();
  }, [isServerOnline, reloadOnce]);

  // NotesContext kuyruk senkronizasyonunu bitirdiğinde reload kapısı açılır
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleSyncComplete = (event) => {
      const { failed = 0, remaining = 0 } = event?.detail || {};
      setSyncPending(remaining > 0 && failed === 0);
      setSyncFailed(failed > 0);

      if (failed > 0) return; // veri kaybı riski: reload yok, kayıt kuyrukta kalır

      let flag = null;
      try { flag = sessionStorage.getItem(OFFLINE_SESSION_KEY); } catch { /* ignore */ }
      if (flag !== '1') return;

      reloadOnce();
    };

    window.addEventListener('notesplus-offline-sync-complete', handleSyncComplete);
    return () => window.removeEventListener('notesplus-offline-sync-complete', handleSyncComplete);
  }, [reloadOnce]);

  // Çevrimdışı durumu için yatay banner YOK — Header göstergesi + tooltip kullanılıyor
  if (!isServerOnline) {
    return null;
  }

  // Bağlantı döndü ama kişisel kuyruk sunucuya yazılamadı
  if (syncFailed) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full shrink-0 z-[60] border-b border-red-500/30 bg-red-950/90 text-red-100"
      >
        <div className="flex items-start gap-3 px-4 py-2.5 sm:px-5 sm:items-center">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5 sm:mt-0" aria-hidden />
          <p className="text-[12px] sm:text-[13px] leading-snug text-red-100/95">
            {t('connectionBanner.syncFailed')}
          </p>
        </div>
      </div>
    );
  }

  // Kuyruk gönderiliyor
  if (syncPending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full shrink-0 z-[60] border-b border-[#3d8ef0]/30 bg-[#0d1424] text-[#c8d8eb]"
      >
        <div className="flex items-start gap-3 px-4 py-2.5 sm:px-5 sm:items-center">
          <Loader2 className="w-4 h-4 text-[#3d8ef0] shrink-0 mt-0.5 sm:mt-0 animate-spin" aria-hidden />
          <p className="text-[12px] sm:text-[13px] leading-snug">
            {t('connectionBanner.syncing')}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default ConnectionMonitorBanner;
